/**
 * 全站版本更新提示：用户长时间打开页面时，发布新版本后可选择立即刷新或稍后处理。
 * 只比较前端构建标识与同源 version.json；网络失败、旧服务器或开发环境没有标识时不误报。
 */
import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";

const CHECK_INTERVAL = 60_000;
const REQUEST_TIMEOUT = 10_000;
const TOAST_ID = "system-update-available";
const reloadCurrentPage = () => window.location.reload();
const COPY = {
  zh: {
    title: "系统已更新",
    description: "发现新版本。立即更新将刷新页面，请先保存未提交的内容。",
    later: "稍后更新",
    update: "立即更新",
  },
  en: {
    title: "System update available",
    description: "A new version is ready. Save any unfinished work before refreshing.",
    later: "Update later",
    update: "Update now",
  },
  es: {
    title: "Actualización disponible",
    description: "Hay una nueva versión. Guarda los cambios pendientes antes de actualizar.",
    later: "Más tarde",
    update: "Actualizar ahora",
  },
};

/** 根据页面语言显示不打断操作的提示；刷新会丢失未保存内容，因此先给用户明确提醒。 */
function UpdateCard({ copy, onLater, onUpdate }) {
  return (
    <section data-testid="system-update-notice" data-no-i18n aria-label={copy.title}
      className="w-full rounded-lg border border-border bg-background p-4 text-foreground shadow-lg">
      <div className="flex items-start gap-3">
        <div aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <RefreshCw className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div role="status" aria-live="polite">
            <p className="text-sm font-semibold">{copy.title}</p>
            <p className="mt-1 text-sm leading-5 text-muted-foreground">{copy.description}</p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" className="min-h-11" onClick={onUpdate} data-testid="system-update-now">{copy.update}</Button>
            <Button type="button" variant="outline" size="sm" className="min-h-11" onClick={onLater} data-testid="system-update-later">{copy.later}</Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * 每分钟、网络恢复以及标签页重新可见时检查已部署版本。
 * currentVersion 是当前页面构建时固定的标识；可从测试传入，生产使用编译期环境变量。
 * 「稍后」只记在当前页面内，不写入持久存储：下次刷新仍在旧版本时会再提示，出现其他版本也会提示。
 */
export default function SystemUpdateNotice({ currentVersion = process.env.REACT_APP_BUILD_VERSION, reloadPage = reloadCurrentPage }) {
  const { lang } = useApp();
  const { pathname } = useLocation();
  const [availableVersion, setAvailableVersion] = useState(null);
  const availableRef = useRef(null);
  const dismissedRef = useRef(null);
  const toastVisibleRef = useRef(false);

  // 公开分包资料页按浏览器首选语言显示中/英，提示也沿用该页规则，避免混用语言。
  const pageLang = pathname.startsWith("/sub-onboard/")
    ? (/^zh(?:[-_]|$)/i.test((navigator.languages?.[0] || navigator.language || "").trim()) ? "zh" : "en")
    : lang;
  const copy = COPY[pageLang] || COPY.zh;

  useEffect(() => {
    // 开发服务器和升级前的旧页面可能没有标识；不能把缺失值当成一次更新。
    if (!currentVersion) return undefined;
    let stopped = false;
    let inFlight = false;
    let controller = null;
    let timeout = null;

    const checkVersion = async () => {
      if (stopped || inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      controller = new AbortController();
      timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      try {
        // 参数与 no-store 同时绕开浏览器及中间缓存，避免用户刷新后仍读到旧版本标识。
        const url = `${process.env.PUBLIC_URL || ""}/version.json?t=${Date.now()}`;
        const response = await fetch(url, { cache: "no-store", signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        const version = typeof data?.version === "string" ? data.version.trim() : "";
        if (stopped || !version) return;
        if (version === currentVersion) {
          // 回滚到当前版本后移除已失效提示；再次发布曾跳过的版本时仍应提醒。
          dismissedRef.current = null;
          if (availableRef.current) {
            availableRef.current = null;
            setAvailableVersion(null);
          }
        } else if (version === dismissedRef.current) {
          // 如果刚提示了更高版本，但服务器又回滚到已选择稍后的版本，撤掉过期提示。
          if (availableRef.current) {
            availableRef.current = null;
            setAvailableVersion(null);
          }
        } else if (version !== availableRef.current) {
          availableRef.current = version;
          setAvailableVersion(version);
        }
      } catch {
        // 断网、超时、404 或旧部署返回 HTML 都不是新版本；下次检查自动恢复。
      } finally {
        window.clearTimeout(timeout);
        controller = null;
        inFlight = false;
      }
    };

    const onVisible = () => { if (document.visibilityState !== "hidden") checkVersion(); };
    checkVersion();
    const interval = window.setInterval(checkVersion, CHECK_INTERVAL);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", checkVersion);
    return () => {
      stopped = true;
      controller?.abort();
      window.clearTimeout(timeout);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", checkVersion);
    };
  }, [currentVersion]);

  useEffect(() => {
    if (!availableVersion) {
      // Sonner 的 dismiss 在下一帧执行；从未显示时不要发送 dismiss，避免误关刚出现的新提示。
      if (toastVisibleRef.current) toast.dismiss(TOAST_ID);
      toastVisibleRef.current = false;
      return;
    }
    const later = () => {
      dismissedRef.current = availableVersion;
      if (availableRef.current === availableVersion) availableRef.current = null;
      setAvailableVersion(null);
    };
    // 复用全局提示栈，常驻且不遮住整个页面；同一版本不会每分钟重新弹出。
    toast.custom(() => <UpdateCard copy={copy} onLater={later} onUpdate={reloadPage} />, {
      id: TOAST_ID,
      duration: Infinity,
      dismissible: false,
    });
    toastVisibleRef.current = true;
  }, [availableVersion, copy, reloadPage]);

  useEffect(() => () => { if (toastVisibleRef.current) toast.dismiss(TOAST_ID); }, []);
  return null;
}
